/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import {modal} from '@app/actions/ModalActionCreators';
import {ScreenRecordingPermissionDeniedModal} from '@app/components/alerts/ScreenRecordingPermissionDeniedModal';
import {ScreenShareSourceModal} from '@app/components/modals/ScreenShareSourceModal';
import {Logger} from '@app/lib/Logger';
import LocalVoiceStateStore from '@app/stores/LocalVoiceStateStore';
import MediaPermissionStore from '@app/stores/MediaPermissionStore';
import VoiceSettingsStore from '@app/stores/VoiceSettingsStore';
import MediaEngineStore from '@app/stores/voice/MediaEngineFacade';
import type {DesktopSource, DisplayMediaRequestInfo} from '@app/types/electron.d';
import {listAudioWindows, supportsPerAppAudioCapture, supportsSystemExcludeAudioCapture} from '@app/utils/AppAudioCapture';
import {checkNativePermission} from '@app/utils/NativePermissions';
import {getElectronAPI, isNativeMacOS} from '@app/utils/NativeUtils';
import {useEffect} from 'react';

const logger = new Logger('ElectronScreenSharePicker');
const DESKTOP_SOURCE_TYPES: Array<'screen' | 'window'> = ['screen', 'window'];

const SCREEN_SHARE_SELECTION_TIMEOUT_MS = 55_000;

interface PromptDesktopSourceSelectionOptions {
	audioRequested: boolean;
	supportsLoopbackAudio?: boolean;
	supportsSystemAudioCapture?: boolean;
}

const promptDesktopSourceSelection = (
	sources: Array<DesktopSource>,
	options: PromptDesktopSourceSelectionOptions,
): Promise<string | null> => {
	return new Promise((resolve) => {
		let resolved = false;
		let selectionTimeoutId: ReturnType<typeof setTimeout> | null = null;

		const clearSelectionTimeout = () => {
			if (selectionTimeoutId !== null) {
				clearTimeout(selectionTimeoutId);
				selectionTimeoutId = null;
			}
		};

		const handleSelection = (sourceId: string | null) => {
			if (resolved) return;
			resolved = true;
			clearSelectionTimeout();
			ModalActionCreators.pop();
			resolve(sourceId);
		};

		selectionTimeoutId = setTimeout(() => handleSelection(null), SCREEN_SHARE_SELECTION_TIMEOUT_MS);

		ModalActionCreators.push(
			modal(() => (
				<ScreenShareSourceModal
					sources={sources}
					audioRequested={options.audioRequested}
					supportsLoopbackAudio={options.supportsLoopbackAudio}
					supportsSystemAudioCapture={options.supportsSystemAudioCapture}
					onSelect={handleSelection}
				/>
			)),
		);
	});
};

// Map a chosen desktopCapturer window source to the OS process id whose audio we should capture.
// The source id is "window:<hwnd>:<n>"; we match that HWND against the audio-window list, falling back
// to the window title (desktopCapturer source name === window title) since HWND encodings can differ.
const resolveWindowProcessId = async (sourceId: string, sourceName: string | undefined): Promise<string | null> => {
	const windows = await listAudioWindows();
	if (windows.length === 0) {
		return null;
	}
	const hwndFromId = sourceId.split(':')[1];
	if (hwndFromId) {
		const byHwnd = windows.find((win) => win.hwnd === hwndFromId);
		if (byHwnd) {
			return byHwnd.processId;
		}
	}
	const trimmedName = sourceName?.trim();
	if (trimmedName) {
		const byTitle = windows.find((win) => win.title.trim() === trimmedName);
		if (byTitle) {
			return byTitle.processId;
		}
		const byPartial = windows.find(
			(win) => win.title.length > 0 && (win.title.includes(trimmedName) || trimmedName.includes(win.title)),
		);
		if (byPartial) {
			return byPartial.processId;
		}
	}
	return null;
};

export const useElectronScreenSharePicker = (): void => {
	useEffect(() => {
		const electronApi = getElectronAPI();
		if (!electronApi || !electronApi.onDisplayMediaRequested) {
			logger.info('Screen share picker unavailable (missing platform handler)');
			return;
		}

		let handlingRequest = false;

		const handleRequest = async (requestId: string, info: DisplayMediaRequestInfo) => {
			if (handlingRequest) {
				electronApi.selectDisplayMediaSource(requestId, null, false);
				return;
			}
			if (!info.videoRequested) {
				logger.warn('Rejecting display media request without a video stream', {
					requestId,
					audioRequested: info.audioRequested,
				});
				electronApi.selectDisplayMediaSource(requestId, null, false);
				return;
			}

			handlingRequest = true;

			try {
				const supportsLoopbackAudio = info.supportsLoopbackAudio ?? false;
				const supportsSystemAudioCapture = info.supportsSystemAudioCapture ?? false;
				const canIncludeAudio = info.audioRequested && (supportsLoopbackAudio || supportsSystemAudioCapture);

				if (isNativeMacOS()) {
					const permission = await checkNativePermission('screen');
					if (permission === 'denied') {
						logger.warn('Screen recording permission denied');
						if (!MediaPermissionStore.isScreenRecordingExplicitlyDenied()) {
							MediaPermissionStore.markScreenRecordingExplicitlyDenied();
							ModalActionCreators.push(modal(() => <ScreenRecordingPermissionDeniedModal />));
						}
						electronApi.selectDisplayMediaSource(requestId, null, false);
						return;
					}
				}

				const sources = await electronApi.getDesktopSources(DESKTOP_SOURCE_TYPES, requestId);
				if (sources.length === 0) {
					logger.warn('No desktop sources available');
					electronApi.selectDisplayMediaSource(requestId, null, false);
					return;
				}

				// Resolve the final selection: when the user picks a window AND "stream this app's audio"
				// mode is on, capture that window's process audio out-of-band (no system loopback) and tell
				// VoiceScreenShareManager which PID to publish. A whole screen, "system audio" mode, or a
				// failed PID lookup falls back to the regular loopback path.
				const finalizeSelection = async (selectedSourceId: string | null): Promise<void> => {
					if (selectedSourceId === null) {
						MediaEngineStore.setPendingScreenShareAudioSource(null);
						electronApi.selectDisplayMediaSource(requestId, null, false);
						return;
					}

					// Decide whether the next share uses NATIVE stream audio (process loopback) instead of the
					// system loopback + echo-canceller. Native covers: the shared window's app (include) in
					// "application" mode, or — when exclude is supported (1.0.4) — all system audio minus Fluxer.
					// The actual include/exclude choice is finalized in VoiceScreenShareManager from the setting.
					if (info.audioRequested && LocalVoiceStateStore.getSelfStreamAudio()) {
						const isWindow = selectedSourceId.startsWith('window:');
						const mode = VoiceSettingsStore.getScreenShareAudioSource();
						const sourceName = sources.find((source) => source.id === selectedSourceId)?.name;
						const windowProcessId = isWindow ? await resolveWindowProcessId(selectedSourceId, sourceName) : null;
						const useNativeAudio =
							(mode === 'application' && windowProcessId !== null && supportsPerAppAudioCapture()) ||
							supportsSystemExcludeAudioCapture();
						if (useNativeAudio) {
							MediaEngineStore.setPendingScreenShareAudioSource({windowProcessId});
							// Video only — no system loopback; the audio is captured natively and published separately.
							electronApi.selectDisplayMediaSource(requestId, selectedSourceId, false);
							logger.info('Using native stream audio', {selectedSourceId, windowProcessId, mode});
							return;
						}
					}

					// Fallback: regular system loopback audio (web, or older desktop for non-app shares).
					MediaEngineStore.setPendingScreenShareAudioSource(null);
					electronApi.selectDisplayMediaSource(requestId, selectedSourceId, canIncludeAudio);
				};

				if (sources.length === 1) {
					await finalizeSelection(sources[0].id);
					return;
				}

				const selectedSourceId = await promptDesktopSourceSelection(sources, {
					audioRequested: info.audioRequested,
					supportsLoopbackAudio,
					supportsSystemAudioCapture,
				});
				await finalizeSelection(selectedSourceId);
			} catch (error) {
				logger.error('Failed to handle display media request', error);
				electronApi.selectDisplayMediaSource(requestId, null, false);
			} finally {
				handlingRequest = false;
			}
		};

		const unsubscribe = electronApi.onDisplayMediaRequested((requestId, info) => {
			void handleRequest(requestId, info);
		});

		return () => {
			unsubscribe();
		};
	}, []);
};
